import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { match } from 'ts-pattern';

import { BedrockAgentType } from '@cloud-wave/neon-common-lib';

import { useScrollManagedContainer } from 'lib/common/hooks/useScrollManagedContainer';

import { useAIContext } from '../../Context';
import { IQActionItems } from '../IQActionsMenu/constants/IQActionItems';
import IQResult from '../IQResult/IQResult';
import IQCursorOrb from '../common/IQCursorOrb/IQCursorOrb';
import SlideInEffect from '../common/SlideInEffect/SlideInEffect';
import { IQDisplayMode, TIQDisplayMode } from '../constants/IQDisplayMode';
import { IQResponseState } from '../constants/IQResponseState';
import { StreamingText } from './components/StreamingText/StreamingText';

import styles from './iq-critique.module.scss';

export default function IQCritique({
  displayMode,
  showAcceptForRewrites = true,
}: {
  displayMode: TIQDisplayMode;
  showAcceptForRewrites?: boolean;
}) {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    state: { response, isStreaming, requestState, selectedAction, streamingState, IQFeatureConfig },
  } = useAIContext();

  const critiqueText = streamingState.feedback ?? [];

  const hasCritique = critiqueText.length > 0;
  const isRethinking = requestState === IQResponseState.RETHINKING;
  const isEvaluateAction = Boolean(IQFeatureConfig[BedrockAgentType.AI_REFINE]?.evaluateEnabled) && selectedAction === IQActionItems.evaluateEnabled.actionID;

  const { scrollToBottom } = useScrollManagedContainer(containerRef);

  const renderFeedback = match({ hasCritique, isStreaming, isRethinking })
    .with({ hasCritique: true }, () => (
      <StreamingText text={critiqueText} onComplete={() => setIsAnimationComplete(true)} />
    ))
    .with({ isStreaming: true, isRethinking: false }, () => <IQCursorOrb speed="SLOW" />)
    .otherwise(() => null);

  useEffect(() => {
    if (requestState === IQResponseState.STANDBY && isEvaluateAction && !hasCritique) {
      setIsAnimationComplete(true);
    }
  }, [requestState, isEvaluateAction, hasCritique]);

  useEffect(() => scrollToBottom({ force: true }), [response, streamingState, isAnimationComplete]);

  return (
    <div
      ref={containerRef}
      className={cx(styles['iq-critique'], {
        [styles['iq-critique--not-stand-alone']]: displayMode !== IQDisplayMode.STAND_ALONE,
      })}
    >
      {renderFeedback}
      <div className={styles['iq-critique__rewrite']}>
        <SlideInEffect>
          <IQResult
            displayMode={displayMode}
            hasFeedback={isEvaluateAction}
            showAcceptForRewrites={showAcceptForRewrites}
            isAnimationComplete={isAnimationComplete}
          />
        </SlideInEffect>
      </div>
    </div>
  );
}
